import React, { useEffect, useState, useCallback, useRef } from "react";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, query, where, getDoc, increment
} from "firebase/firestore";
import { getAuth, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { useBranding } from "../../context/BrandingContext";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import { getMapCenter } from "../../utils/timezoneCoords";
import { startOfDayInTimezone } from "../../utils/dateFormat";
import { GOOGLE_MAPS_KEY, GOOGLE_MAPS_LIBRARIES } from "../../utils/googleMapsConfig";
import { isPointInPolygon } from "geolib";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";
import {
  Users, BarChart2, User,
  MapPin, Phone, ChevronDown,
  Pencil, Trash2, Search, AlertTriangle, CheckCircle,
  Loader2, Lock, Eye, EyeOff, Navigation, Save,
  LogOut, X, HardHat, Mail, Plus, Map as MapIcon,
  Building2, Home, Landmark, Crosshair, MessageSquare, Send, Moon,
} from "lucide-react";

// ── Nav pages ──────────────────────────────────────────────────────────────
const PAGES = [
  { id: "map",         Icon: MapIcon,  labelKey: "op_dash_map"         },
  { id: "my_clients",  Icon: Users,    labelKey: "op_dash_my_clients"  },
  { id: "statistics",  Icon: BarChart2,labelKey: "op_dash_statistics"  },
  { id: "profile",     Icon: User,     labelKey: "op_dash_profile"     },
];

const DEFAULT_CENTER = { lat: 33.5731, lng: -7.5898 }; // Casablanca fallback

function OperatorDashboard() {
  const { branding } = useBranding();
  const { currentUser, companyId, logout } = useAuth();
  const { t } = useLanguage();
  const [activePage, setActivePage] = useState("map");
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem("pinpoint_dark_mode") === "true");

  function toggleDarkMode() {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem("pinpoint_dark_mode", String(next));
    if (next) document.documentElement.classList.add("dark");
    else      document.documentElement.classList.remove("dark");
  }

  React.useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add("dark");
    else            document.documentElement.classList.remove("dark");
  }, []); // eslint-disable-line

  return (
    // ── LAYOUT: full-height flex column — navbar top, content fills rest, bottom tab bar fixed ──
    <div className={`min-h-screen flex flex-col ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>

      {/* ── Top navbar ──────────────────────────────────────────────────── */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.platform_name} className="w-6 h-6 object-contain rounded" />
            ) : (
              <HardHat className="w-5 h-5 text-orange-500" />
            )}
            <span className="font-bold text-gray-800 text-base">{(branding.platform_name || "Pinpoint").toUpperCase()}</span>
            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
              {t("role_operator")}
            </span>
          </div>
          <button onClick={logout}
            className="flex items-center gap-1.5 text-sm text-red-500 font-medium py-1.5 px-3 rounded-lg active:bg-red-50">
            <LogOut className="w-4 h-4" />
            {t("logout")}
          </button>
        </div>
      </nav>

      {/* ── Page content ────────────────────────────────────────────────── */}
      {/* Map page: no padding, no scroll — full height between navbar and tab bar */}
      <main className={`flex-1 ${activePage === "map" ? "overflow-hidden" : "pb-24 overflow-y-auto"}`}>
        {activePage === "map"        && <MapPage        companyId={companyId} currentUser={currentUser} t={t} />}
        {activePage === "my_clients" && <MyClientsPage  companyId={companyId} currentUser={currentUser} t={t} />}
        {activePage === "statistics" && <StatisticsPage companyId={companyId} currentUser={currentUser} t={t} />}
        {activePage === "profile"    && <ProfilePage    currentUser={currentUser} t={t} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
      </main>

      {/* ── Bottom tab bar ───────────────────────────────────────────────── */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
        <div className="flex">
          {PAGES.map(({ id, Icon, label, labelKey }) => {
            const active = activePage === id;
            return (
              <button key={id} onClick={() => setActivePage(id)}
                className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors ${
                  active ? "text-orange-600" : "text-gray-400 active:bg-gray-50"
                }`}>
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-none">{label || t(labelKey)}</span>
                {active && <span className="w-1 h-1 rounded-full bg-orange-500" />}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE 1 — Map (full-screen satellite, all clients, + Add Client panel)
// ═══════════════════════════════════════════════════════════════════
function MapPage({ companyId, currentUser, t }) {
  // ── Map state ────────────────────────────────────────────────────
  const [mapType,        setMapType]        = useState("satellite");
  const [mapCenter,      setMapCenter]      = useState(DEFAULT_CENTER);
  const [myPos,          setMyPos]          = useState(null);
  const [clients,        setClients]        = useState([]);
  const [sections,       setSections]       = useState([]);
  const [selected,       setSelected]       = useState(null);
  const [visitResetDays, setVisitResetDays] = useState(1);
  const [clientTypes,    setClientTypes]    = useState([]);
  const mapRef          = useRef(null);
  const longPressTimer  = useRef(null);

  // ── Panel (Add / Edit) state ──────────────────────────────────────
  const [panelOpen,   setPanelOpen]   = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);       // null = new client
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");
  // eslint-disable-next-line no-unused-vars
  const [success,     setSuccess]     = useState("");
  const [locating,    setLocating]    = useState(false);
  const [pinPos,      setPinPos]      = useState(null);
  const [panelCenter, setPanelCenter] = useState(DEFAULT_CENTER);
  const panelMapRef = useRef(null);

  const emptyForm = { firstName: "", lastName: "", phone: "", address: "", client_type: "", notes: "" };
  const [form, setForm] = useState(emptyForm);

  // ── Send Note state ───────────────────────────────────────────────
  const [noteModal,   setNoteModal]   = useState(false);
  const [noteClient,  setNoteClient]  = useState(null);
  const [noteSaving,  setNoteSaving]  = useState(false);
  const [noteError,   setNoteError]   = useState("");
  const [noteSuccess, setNoteSuccess] = useState("");
  const emptyNote = { message: "", priority: "normal" };
  const [noteForm, setNoteForm] = useState(emptyNote);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // ── Load all clients + sections on mount ──────────────────────────
  useEffect(() => {
    async function load() {
      try {
        const [clientsSnap, sectionsSnap, companySnap, configSnap] = await Promise.all([
          getDocs(collection(db, "companies", companyId, "clients")),
          getDocs(collection(db, "companies", companyId, "sections")),
          getDoc(doc(db, "companies", companyId)),
          getDoc(doc(db, "companies", companyId, "config", "settings")),
        ]);
        setClients(clientsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setSections(sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        if (companySnap.exists()) {
          setVisitResetDays(companySnap.data().visit_reset_days ?? 1);
        }
        // Set initial map center based on company timezone (geolocation overrides if available)
        const cfg = configSnap.exists() ? configSnap.data() : {};
        const timezone = cfg.timezone || "Asia/Dubai";
        const tzCenter = getMapCenter(timezone);
        setMapCenter({ lat: tzCenter.lat, lng: tzCenter.lng });
        // Load client types defined by admin
        const types = cfg.client_types;
        setClientTypes(Array.isArray(types) && types.length > 0
          ? types
          : ["Residential", "Commercial", "Government", "Other"]);
      } catch (err) {
        console.error("map load error:", err.message);
      }
    }
    load();
  }, [companyId]);

  // ── Get operator GPS on mount ────────────────────────────────────
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMyPos(p);
        setMapCenter(p);
      },
      () => {} // silent fail — fallback to DEFAULT_CENTER
    );
  }, []);

  // ── Zone auto-detect ────────────────────────────────────────────
  function detectSectionFromGPS(lat, lng) {
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) return "";
    for (const section of sections) {
      if (!section.polygon || section.polygon.length < 3) continue;
      const polygon = section.polygon.map(p => ({ latitude: p.lat, longitude: p.lng }));
      if (isPointInPolygon({ latitude: lat, longitude: lng }, polygon)) return section.id;
    }
    return "";
  }

  // ── Open panel for new client (pos optional — supplied by long-press) ──
  function openAddPanel(pos) {
    setEditTarget(null);
    setForm(emptyForm);
    setError("");
    setSuccess("");
    const center = pos || myPos || DEFAULT_CENTER;
    setPinPos(center);
    setPanelCenter(center);
    setPanelOpen(true);
  }

  // ── Open panel for editing existing client ───────────────────────
  function openEditPanel(client) {
    setSelected(null);
    setEditTarget(client);
    setForm({
      firstName:   client.first_name  || "",
      lastName:    client.last_name   || "",
      phone:       client.phone       || "",
      address:     client.address     || "",
      client_type: client.client_type || "",
      notes:       client.notes       || "",
    });
    setError("");
    setSuccess("");
    if (client.location?.lat && client.location?.lng) {
      const pos = { lat: client.location.lat, lng: client.location.lng };
      setPinPos(pos);
      setPanelCenter(pos);
    } else {
      const center = myPos || DEFAULT_CENTER;
      setPinPos(center);
      setPanelCenter(center);
    }
    setPanelOpen(true);
  }

  // ── Close panel ──────────────────────────────────────────────────
  function closePanel() {
    setPanelOpen(false);
    setEditTarget(null);
    setForm(emptyForm);
    setPinPos(null);
    setError("");
  }

  // ── Use my location inside panel ────────────────────────────────
  function usePanelLocation() {
    if (!navigator.geolocation) return setError(t("op_client_gps_error"));
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setPinPos(p);
        setPanelCenter(p);
        if (panelMapRef.current) panelMapRef.current.panTo(p);
        setLocating(false);
      },
      () => { setError(t("op_client_gps_error")); setLocating(false); }
    );
  }

  const onPanelMapClick  = useCallback((e) => { setPinPos({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }, []);
  const onPanelDragEnd   = useCallback((e) => { setPinPos({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }, []);

  // ── Save client ──────────────────────────────────────────────────
  async function handleSave() {
    setError("");
    if (!form.firstName.trim()) return setError(t("form_first_name") + " " + t("form_required"));
    if (!form.phone.trim())     return setError(t("form_phone") + " " + t("form_required"));

    setSaving(true);
    try {
      const autoSection = (pinPos?.lat && pinPos?.lng)
        ? detectSectionFromGPS(pinPos.lat, pinPos.lng)
        : "";

      const data = {
        first_name:   form.firstName.trim(),
        last_name:    form.lastName.trim(),
        name:         `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        phone:        form.phone.trim(),
        address:      form.address.trim(),
        section:      autoSection,
        client_type:  form.client_type || "",
        notes:        form.notes.trim(),
        location:     (pinPos?.lat && pinPos?.lng) ? { lat: pinPos.lat, lng: pinPos.lng } : null,
        updated_at:   serverTimestamp(),
      };

      if (editTarget) {
        await updateDoc(doc(db, "companies", companyId, "clients", editTarget.id), data);
        // Update marker in local state immediately
        setClients(prev => prev.map(c => c.id === editTarget.id ? { ...c, ...data } : c));
        closePanel();
      } else {
        data.created_by      = currentUser.uid;
        data.created_by_name = currentUser.displayName || currentUser.email;
        data.created_at      = serverTimestamp();
        const ref = await addDoc(collection(db, "companies", companyId, "clients"), data);
        // Counter update — separate try/catch so it never blocks main operation
        try {
          await updateDoc(doc(db, "companies", companyId), { client_count: increment(1) });
        } catch (counterErr) {
          console.error("Counter update failed:", counterErr.message);
        }
        // Add new marker immediately without reload
        setClients(prev => [...prev, { id: ref.id, ...data }]);
        closePanel();
      }
    } catch (err) {
      setError("Error: " + err.message);
    }
    setSaving(false);
  }

  // ── Navigate to client in Google Maps ───────────────────────────
  function navigateTo(client) {
    if (!client.location?.lat || !client.location?.lng) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${client.location.lat},${client.location.lng}`;
    window.open(url, "_blank");
  }

  // ── Note modal ───────────────────────────────────────────────────
  function openNoteModal(client) {
    setSelected(null);
    setNoteClient(client);
    setNoteForm(emptyNote);
    setNoteError("");
    setNoteSuccess("");
    setNoteModal(true);
  }

  async function sendNote() {
    if (!noteForm.message.trim()) return setNoteError("Please enter a message.");
    setNoteSaving(true);
    setNoteError("");
    try {
      const clientName = noteClient.name ||
        `${noteClient.first_name || ""} ${noteClient.last_name || ""}`.trim();
      await addDoc(collection(db, "companies", companyId, "notes"), {
        client_id:       noteClient.id,
        client_name:     clientName,
        client_phone:    noteClient.phone    || "",
        client_location: noteClient.location || null,
        message:         noteForm.message.trim(),
        priority:        noteForm.priority,
        sent_by:         currentUser.uid,
        sent_by_name:    currentUser.displayName || currentUser.email,
        sent_by_role:    "operator",
        created_at:      serverTimestamp(),
        read:            false,
      });
      setNoteSuccess("Note sent to admin.");
      setTimeout(() => {
        setNoteModal(false);
        setNoteClient(null);
        setNoteSuccess("");
      }, 1500);
    } catch (err) {
      setNoteError("Error: " + err.message);
    }
    setNoteSaving(false);
  }

  // ── Locate me — re-center map to operator GPS ────────────────────
  function locateMe() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMyPos(p);
        setMapCenter(p);
        if (mapRef.current) mapRef.current.panTo(p);
      },
      () => {}
    );
  }

  // ── Client type icon helper ──────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  function ClientTypeIcon({ type }) {
    if (type === "Commercial")  { return <Building2  className="w-3.5 h-3.5" />; }
    if (type === "Government")  { return <Landmark   className="w-3.5 h-3.5" />; }
    if (type === "Residential") { return <Home       className="w-3.5 h-3.5" />; }
    return <MapPin className="w-3.5 h-3.5" />;
  }

  // ── Clients with valid GPS (for map markers) ─────────────────────
  const mappableClients = clients.filter(c => c.location?.lat && c.location?.lng);

  return (
    // ── Outer container: explicit height = viewport minus navbar (57px) minus tab bar (57px)
    // This is the same pattern as admin LiveMap. h-full alone does NOT work without a fixed parent height.
    <div className="relative overflow-hidden" style={{ height: "calc(100vh - 114px)" }}>

      {/* ── Full-screen map ───────────────────────────────────────── */}
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={mapCenter}
          zoom={14}
          mapTypeId={mapType}
          onLoad={map => {
            mapRef.current = map;
            const div = map.getDiv();

            // OverlayView lets us convert container pixel → LatLng accurately
            const overlay = new window.google.maps.OverlayView();
            overlay.draw = () => {};
            overlay.setMap(map);

            const cancelPress = () => {
              if (longPressTimer.current) {
                clearTimeout(longPressTimer.current);
                longPressTimer.current = null;
              }
            };

            function startTimer(clientX, clientY) {
              cancelPress();
              const rect = div.getBoundingClientRect();
              const x = clientX - rect.left;
              const y = clientY - rect.top;
              longPressTimer.current = setTimeout(() => {
                longPressTimer.current = null;
                const proj = overlay.getProjection();
                if (!proj) return;
                const ll = proj.fromContainerPixelToLatLng(
                  new window.google.maps.Point(x, y)
                );
                if (ll) openAddPanel({ lat: ll.lat(), lng: ll.lng() });
              }, 2000);
            }

            const captureOpts = { passive: true, capture: true };

            // ── Mobile touch ─────────────────────────────────────────
            div.addEventListener("touchstart", (e) => {
              if (e.touches.length > 1) { cancelPress(); return; }
              const t = e.touches[0];
              startTimer(t.clientX, t.clientY);
            }, captureOpts);
            div.addEventListener("touchmove",   cancelPress, captureOpts);
            div.addEventListener("touchend",    cancelPress, captureOpts);
            div.addEventListener("touchcancel", cancelPress, captureOpts);

            // ── Desktop mouse ────────────────────────────────────────
            div.addEventListener("mousedown", (e) => {
              if (e.button !== 0) return;
              startTimer(e.clientX, e.clientY);
            }, { capture: true });
            div.addEventListener("mousemove", cancelPress, { capture: true });
            div.addEventListener("mouseup",   cancelPress, { capture: true });
          }}
          onClick={() => setSelected(null)}
          options={{
            streetViewControl:  false,
            mapTypeControl:     false,
            fullscreenControl:  false,
            zoomControl:        true,
            zoomControlOptions: { position: 7 }, // RIGHT_CENTER
          }}
        >
          {/* All client markers — orange pins */}
          {mappableClients.map(client => (
            <Marker
              key={client.id}
              position={{ lat: client.location.lat, lng: client.location.lng }}
              onClick={() => setSelected(client)}
              title={client.name || `${client.first_name} ${client.last_name}`}
              icon={{
                url: (() => {
                  const s = client.last_status;
                  if (s === "arrived" || s === "done") {
                    const visitedAt = client.last_visited_at;
                    if (visitedAt) {
                      const d = visitedAt.toDate ? visitedAt.toDate() : new Date(visitedAt);
                      const daysSinceVisit = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
                      if (daysSinceVisit < visitResetDays) {
                        return "/icons/green-pin.png";
                      }
                    }
                  }
                  return "/icons/red-pin.png";
                })(),
                scaledSize: new window.google.maps.Size(18, 18),
                anchor: new window.google.maps.Point(9, 18),
              }}
            />
          ))}

          {/* Operator GPS marker — distinct blue dot */}
          {myPos && (
            <Marker
              position={myPos}
              title="My location"
              icon={{
                url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="16" fill="#2563eb" stroke="white" stroke-width="3"/>
                    <circle cx="18" cy="18" r="6" fill="white"/>
                  </svg>`),
                scaledSize: { width: 36, height: 36 },
                anchor: { x: 18, y: 18 },
              }}
            />
          )}

          {/* InfoWindow for selected client */}
          {selected && selected.location?.lat && selected.location?.lng && (
            <InfoWindow
              position={{ lat: selected.location.lat, lng: selected.location.lng }}
              onCloseClick={() => setSelected(null)}
            >
              <div style={{ minWidth: 180, maxWidth: 220, fontFamily: "sans-serif" }}>
                <p style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: "#1f2937" }}>
                  {selected.name || `${selected.first_name || ""} ${selected.last_name || ""}`.trim()}
                </p>
                {selected.phone && (
                  <p style={{ fontSize: 12, color: "#4b5563", marginBottom: 2 }}>📞 {selected.phone}</p>
                )}
                {selected.client_type && (
                  <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>🏷 {selected.client_type}</p>
                )}
                {selected.address && (
                  <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>📍 {selected.address}</p>
                )}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button
                    onClick={() => navigateTo(selected)}
                    style={{ flex: 1, background: "#2563eb", color: "white", border: "none", borderRadius: 8, padding: "7px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {t("op_dash_navigate")}
                  </button>
                  <button
                    onClick={() => openEditPanel(selected)}
                    style={{ flex: 1, background: "#f97316", color: "white", border: "none", borderRadius: 8, padding: "7px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {t("op_dash_edit")}
                  </button>
                  <button
                    onClick={() => openNoteModal(selected)}
                    style={{ width: "100%", background: "#7c3aed", color: "white", border: "none", borderRadius: 8, padding: "7px 0", fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>
                    {t("op_dash_send_note")}
                  </button>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      ) : (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      )}

      {/* ── Floating: Map type toggle — top left ───────────────────── */}
      <div className="absolute top-3 left-3 z-10 pointer-events-auto flex gap-1">
        {[
          { id: "roadmap",   label: t("sup_dash_map")       },
          { id: "satellite", label: t("sup_dash_satellite") },
          { id: "hybrid",    label: "Hybrid"                },
        ].map(type => (
          <button
            key={type.id}
            onClick={() => setMapType(type.id)}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-medium shadow transition ${
              mapType === type.id
                ? "bg-yellow-500 text-white"
                : "bg-white/90 backdrop-blur-sm text-gray-700 hover:bg-white border border-white/50"
            }`}>
            {type.label}
          </button>
        ))}
      </div>

      {/* ── Floating: + Add Client — top center ──────────────────── */}
      <div className="absolute z-10 pointer-events-auto" style={{ top: 16, left: "50%", transform: "translateX(-50%)" }}>
        <button
          onClick={() => openAddPanel()}
          className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 text-gray-900 font-bold text-sm px-5 py-2.5 rounded-full shadow-lg">
          <Plus className="w-4 h-4" />
          {t("op_dash_add_client")}
        </button>
      </div>

      {/* ── Floating: Locate Me — right side, above zoom controls ── */}
      <div className="absolute z-10 pointer-events-auto" style={{ bottom: 120, right: 10 }}>
        <button
          onClick={locateMe}
          className="w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center active:bg-gray-50">
          <Crosshair className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* ── Client count badge — bottom left ─────────────────────── */}
      <div className="absolute bottom-4 left-4 z-10 pointer-events-none">
        <div className="bg-white/90 backdrop-blur-sm text-gray-600 text-xs font-medium px-3 py-1.5 rounded-xl shadow border border-white/50">
          {mappableClients.length} client{mappableClients.length !== 1 ? "s" : ""} on map
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          SLIDE-UP PANEL — Add / Edit Client
          z-index 50, positioned inside the same container
      ══════════════════════════════════════════════════════════════ */}
      {panelOpen && (
        <div className="absolute inset-0 z-50 flex flex-col justify-end">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={closePanel} />

          {/* Panel sheet */}
          <div className="relative bg-white rounded-t-3xl w-full max-h-[95%] flex flex-col overflow-hidden shadow-2xl">

            {/* Panel header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
              <h2 className="text-lg font-bold text-gray-800">
                {editTarget ? t("op_client_edit_title") : t("op_client_title")}
              </h2>
              <button onClick={closePanel}
                className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Scrollable form content */}
            <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-4">

              {/* Error feedback */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 shrink-0" />{error}
                </div>
              )}

              {/* First + Last name */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    {t("op_client_first_name")} <span className="text-red-400">*</span>
                  </label>
                  <input type="text" value={form.firstName}
                    onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400"
                    placeholder="First name" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    {t("op_client_last_name")}
                  </label>
                  <input type="text" value={form.lastName}
                    onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400"
                    placeholder="Last name" />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {t("op_client_phone")} <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="tel" value={form.phone}
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full pl-11 border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400"
                    placeholder="+212 6xx xxx xxx" />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {t("op_client_address")}
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-3.5 w-4 h-4 text-gray-400" />
                  <input type="text" value={form.address}
                    onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                    className="w-full pl-11 border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400"
                    placeholder="Street, city..." />
                </div>
              </div>

              {/* Client Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_dash_client_type")}</label>
                <div className="relative">
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select value={form.client_type}
                    onChange={e => setForm(p => ({ ...p, client_type: e.target.value }))}
                    className="w-full pr-10 border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400 appearance-none bg-white">
                    <option value="">—</option>
                    {(clientTypes || []).map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {t("op_client_notes")}
                </label>
                <textarea value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                  placeholder={t("op_client_notes_ph")} />
              </div>

              {/* ── Map pin picker — satellite, large ──────────────── */}
              <div className="rounded-2xl overflow-hidden border border-gray-200">
                <div className="px-4 pt-3 pb-2 flex items-center justify-between bg-gray-50 border-b border-gray-100">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">{t("op_client_pin_title")}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t("op_client_pin_hint")}</p>
                  </div>
                  <button onClick={usePanelLocation} disabled={locating}
                    className="flex items-center gap-1.5 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 px-3 py-2 rounded-xl active:bg-orange-100 disabled:opacity-60">
                    {locating
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Navigation className="w-4 h-4" />
                    }
                    {locating ? t("op_client_locating") : t("op_client_use_gps")}
                  </button>
                </div>

                {/* Pin map — satellite, tall */}
                <div style={{ height: 280 }}>
                  {isLoaded ? (
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      center={panelCenter}
                      zoom={16}
                      mapTypeId="satellite"
                      onLoad={map => { panelMapRef.current = map; }}
                      onClick={onPanelMapClick}
                      options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                    >
                      {pinPos && (
                        <Marker
                          position={pinPos}
                          draggable={true}
                          onDragEnd={onPanelDragEnd}
                        />
                      )}
                    </GoogleMap>
                  ) : (
                    <div className="h-full flex items-center justify-center bg-gray-100">
                      <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                    </div>
                  )}
                </div>

                {/* Coords */}
                {pinPos && (
                  <div className="px-4 py-2 bg-orange-50 border-t border-orange-100">
                    <p className="text-xs text-orange-600 font-medium flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {pinPos.lat.toFixed(6)}, {pinPos.lng.toFixed(6)}
                    </p>
                  </div>
                )}
              </div>

              {/* Save button */}
              <button onClick={handleSave} disabled={saving}
                className="w-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:opacity-60 text-white font-bold text-lg py-4 rounded-2xl transition flex items-center justify-center gap-2 shadow-md shadow-orange-200">
                {saving
                  ? <><Loader2 className="w-5 h-5 animate-spin" />{t("op_client_saving")}</>
                  : <><Save className="w-5 h-5" />{editTarget ? t("op_client_update") : t("op_client_save")}</>
                }
              </button>

            </div>{/* end scrollable */}
          </div>{/* end panel sheet */}
        </div>
      )}{/* end panelOpen */}

      {/* ══════════════════════════════════════════════════════════════
          SEND NOTE MODAL
      ══════════════════════════════════════════════════════════════ */}
      {noteModal && (
        <div className="absolute inset-0 z-50 flex items-end sm:items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setNoteModal(false)} />

          {/* Modal card */}
          <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl">

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                <h2 className="text-base font-bold text-gray-800">{t("op_dash_send_note")}</h2>
              </div>
              <button onClick={() => setNoteModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="px-5 pb-6 space-y-4">
              {/* Client name — read only */}
              <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600 font-medium">
                {noteClient?.name || `${noteClient?.first_name || ""} ${noteClient?.last_name || ""}`.trim()}
              </div>

              {/* Feedback */}
              {noteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 shrink-0" />{noteError}
                </div>
              )}
              {noteSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 shrink-0" />{noteSuccess}
                </div>
              )}

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {t("op_dash_note_message")} <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={noteForm.message}
                  onChange={e => setNoteForm(p => ({ ...p, message: e.target.value }))}
                  rows={4}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                  placeholder={t("vendor_note_describe_ph")} />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_dash_note_priority")}</label>
                <div className="relative">
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select
                    value={noteForm.priority}
                    onChange={e => setNoteForm(p => ({ ...p, priority: e.target.value }))}
                    className="w-full pr-10 border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-400 appearance-none bg-white">
                    <option value="normal">{t("op_dash_note_normal")}</option>
                    <option value="urgent">{t("op_dash_note_urgent")}</option>
                  </select>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-1">
                <button onClick={() => setNoteModal(false)}
                  className="flex-1 border border-gray-200 text-gray-600 font-medium py-3.5 rounded-2xl active:bg-gray-50">
                  {t("op_dash_cancel")}
                </button>
                <button onClick={sendNote} disabled={noteSaving}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition">
                  {noteSaving
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Send className="w-4 h-4" />
                  }
                  {noteSaving ? t("op_dash_note_sending") : t("op_dash_note_send")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}{/* end noteModal */}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE 2 — My Clients
// ═══════════════════════════════════════════════════════════════════
function MyClientsPage({ companyId, currentUser, t }) {
  const [clients,     setClients]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState("");
  const [deleting,    setDeleting]    = useState(false);
  const [clientTypes, setClientTypes] = useState([]);

  // Edit modal
  const [editModal,    setEditModal]    = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [editForm,     setEditForm]     = useState({});
  const [sections,     setSections]     = useState([]);
  const [saving,       setSaving]       = useState(false);

  // Delete modal
  const [deleteModal,  setDeleteModal]  = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!companyId || !currentUser?.uid) return;
    async function load() {
      setLoading(true);
      try {
        const [snap, secSnap, configSnap] = await Promise.all([
          getDocs(collection(db, "companies", companyId, "clients")),
          getDocs(collection(db, "companies", companyId, "sections")),
          getDoc(doc(db, "companies", companyId, "config", "settings")),
        ]);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort newest first in JavaScript
        list.sort((a, b) => {
          const aTime = a.created_at?.toDate ? a.created_at.toDate() : new Date(a.created_at || 0);
          const bTime = b.created_at?.toDate ? b.created_at.toDate() : new Date(b.created_at || 0);
          return bTime - aTime;
        });
        setClients(list);
        setSections(secSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        const types = configSnap.exists() ? configSnap.data().client_types : null;
        setClientTypes(Array.isArray(types) && types.length > 0
          ? types
          : ["Residential", "Commercial", "Government", "Other"]);
      } catch (err) {
        setError("Error: " + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [companyId, currentUser.uid]);

  function flash(msg, isError = false) {
    if (isError) { setError(msg); setTimeout(() => setError(""), 4000); }
    else         { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); }
  }

  function getSectionName(id) {
    return sections.find(s => s.id === id)?.name || id || "—";
  }

  function openEdit(client) {
    setEditTarget(client);
    setEditForm({
      firstName:   client.first_name  || "",
      lastName:    client.last_name   || "",
      phone:       client.phone       || "",
      address:     client.address     || "",
      client_type: client.client_type || "",
      notes:       client.notes       || "",
    });
    setEditModal(true);
  }

  async function handleEditSave() {
    if (!editForm.firstName.trim()) return setError(t("form_first_name") + " " + t("form_required"));
    if (!editForm.phone.trim())     return setError(t("form_phone") + " " + t("form_required"));
    setSaving(true);
    try {
      const data = {
        first_name:  editForm.firstName.trim(),
        last_name:   editForm.lastName.trim(),
        name:        `${editForm.firstName.trim()} ${editForm.lastName.trim()}`.trim(),
        phone:       editForm.phone.trim(),
        address:     editForm.address.trim(),
        client_type: editForm.client_type || "",
        notes:       editForm.notes.trim(),
        updated_at:  serverTimestamp(),
      };
      await updateDoc(doc(db, "companies", companyId, "clients", editTarget.id), data);
      setClients(prev => prev.map(c => c.id === editTarget.id ? { ...c, ...data } : c));
      setEditModal(false);
      flash(t("op_client_saved"));
    } catch (err) { setError("Error: " + err.message); }
    setSaving(false);
  }

  function askDelete(client) { setDeleteTarget(client); setDeleteModal(true); }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "companies", companyId, "clients", deleteTarget.id));
      // Counter update — separate try/catch so it never blocks main operation
      try {
        await updateDoc(doc(db, "companies", companyId), { client_count: increment(-1) });
      } catch (counterErr) {
        console.error("Counter update failed:", counterErr.message);
      }
      setClients(prev => prev.filter(c => c.id !== deleteTarget.id));
      setDeleteModal(false);
      setDeleteTarget(null);
      flash(t("delete") + " ✅");
    } catch (err) { flash("Error: " + err.message, true); }
    setDeleting(false);
  }

  const filtered = clients.filter(c =>
    `${c.first_name} ${c.last_name} ${c.phone}`
      .toLowerCase().includes(search.toLowerCase())
  );

  function formatDate(ts) {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  }

  if (loading) return (
    <div className="p-4 flex flex-col items-center justify-center py-20 gap-3">
      <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
      <p className="text-gray-400 text-sm">{t("loading")}</p>
    </div>
  );

  return (
    <div className="p-4 space-y-4 max-w-lg mx-auto">

      <h1 className="text-xl font-bold text-gray-800 pt-1">{t("op_my_clients_title")}</h1>

      {/* Feedback */}
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 text-sm flex gap-2"><CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />{success}</div>}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t("op_my_clients_search")}
          className="w-full pl-11 border border-gray-200 rounded-xl px-4 py-3 text-base bg-white focus:outline-none focus:ring-2 focus:ring-orange-400" />
      </div>

      {/* Count */}
      <p className="text-sm text-gray-400">
        {filtered.length} client{filtered.length !== 1 ? "s" : ""}
      </p>

      {/* Empty */}
      {clients.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <Users className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">{t("op_my_clients_empty")}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <Search className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">{t("no_data")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(client => (
            <div key={client.id} className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 text-base">
                    {client.first_name} {client.last_name}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                    <p className="text-sm text-gray-500">{client.phone}</p>
                  </div>
                  {client.address && (
                    <div className="flex items-start gap-1 mt-1">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                      <p className="text-sm text-gray-400 truncate">{client.address}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {client.section && (
                      <span className="text-xs bg-orange-50 text-orange-600 border border-orange-100 px-2 py-0.5 rounded-full">
                        {getSectionName(client.section)}
                      </span>
                    )}
                    <span className="text-xs text-gray-300">
                      {t("op_my_clients_date")}: {formatDate(client.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(client)}
                    className="p-2.5 rounded-xl text-gray-400 hover:text-blue-600 active:bg-blue-50">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => askDelete(client)}
                    className="p-2.5 rounded-xl text-gray-400 hover:text-red-600 active:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Edit Modal ───────────────────────────────────────────────── */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditModal(false)} />
          <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-800">{t("op_client_edit_title")}</h2>
              <button onClick={() => setEditModal(false)} className="p-2 rounded-xl text-gray-400 active:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_client_first_name")} <span className="text-red-400">*</span></label>
                  <input type="text" value={editForm.firstName}
                    onChange={e => setEditForm(p => ({ ...p, firstName: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_client_last_name")}</label>
                  <input type="text" value={editForm.lastName}
                    onChange={e => setEditForm(p => ({ ...p, lastName: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_client_phone")} <span className="text-red-400">*</span></label>
                <input type="tel" value={editForm.phone}
                  onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_client_address")}</label>
                <input type="text" value={editForm.address}
                  onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_dash_client_type")}</label>
                <select value={editForm.client_type}
                  onChange={e => setEditForm(p => ({ ...p, client_type: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white appearance-none">
                  <option value="">—</option>
                  {(clientTypes || []).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_client_notes")}</label>
                <textarea value={editForm.notes} rows={2}
                  onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none" />
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setEditModal(false)}
                  className="flex-1 border border-gray-200 text-gray-600 font-medium py-3.5 rounded-2xl active:bg-gray-50">
                  {t("cancel")}
                </button>
                <button onClick={handleEditSave} disabled={saving}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? t("op_client_updating") : t("op_client_update")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Modal ─────────────────────────────────────────────── */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteModal(false)} />
          <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl p-5 space-y-4">
            <h2 className="text-base font-bold text-gray-800">{t("op_client_delete_title")}</h2>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-700">{deleteTarget?.first_name} {deleteTarget?.last_name}</p>
                <p className="text-xs text-red-500 mt-1">{t("op_client_delete_msg")}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteModal(false)}
                className="flex-1 border border-gray-200 text-gray-600 font-medium py-3.5 rounded-2xl active:bg-gray-50">
                {t("cancel")}
              </button>
              <button onClick={confirmDelete} disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? t("deleting") : t("yes_delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE 3 — My Statistics
// ═══════════════════════════════════════════════════════════════════
function StatisticsPage({ companyId, currentUser, t }) {
  const [clients,         setClients]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [deleted,         setDeleted]         = useState(0); // we track via a counter field
  const [companyTimezone, setCompanyTimezone] = useState("Asia/Dubai");

  useEffect(() => {
    if (!companyId || !currentUser?.uid) return;
    async function load() {
      setLoading(true);
      try {
        const q = query(
          collection(db, "companies", companyId, "clients"),
          where("created_by", "==", currentUser.uid)
        );
        const snap = await getDocs(q);
        setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Read deleted count from operator's user doc
        const userSnap = await getDoc(doc(db, "companies", companyId, "users", currentUser.uid));
        if (userSnap.exists()) setDeleted(userSnap.data().deleted_clients_count || 0);

        // Load company timezone for accurate date boundaries
        const configSnap = await getDoc(doc(db, "companies", companyId, "config", "settings"));
        if (configSnap.exists()) setCompanyTimezone(configSnap.data().timezone || "Asia/Dubai");
      } catch (err) {
        console.error("stats load error:", err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [companyId, currentUser.uid]);

  // ── Compute stats — use company timezone for all date boundaries ──
  const today     = startOfDayInTimezone(companyTimezone);
  const dayOfWeek = parseInt(new Date().toLocaleDateString("en-US", { timeZone: companyTimezone, weekday: "numeric" }), 10) - 1;
  const weekStart = new Date(today.getTime() - dayOfWeek * 86400000);

  function getDate(ts) {
    if (!ts) return null;
    return ts.toDate ? ts.toDate() : new Date(ts);
  }

  const totalClients  = clients.length;
  const todayClients  = clients.filter(c => { const d = getDate(c.created_at); return d && d >= today; }).length;
  const weekClients   = clients.filter(c => { const d = getDate(c.created_at); return d && d >= weekStart; }).length;

  // ── Daily chart: last 7 days — boundaries in company timezone ──
  const dailyData = Array.from({ length: 7 }, (_, i) => {
    const day  = new Date(today.getTime() - (6 - i) * 86400000);
    const next = new Date(day.getTime() + 86400000);
    const count = clients.filter(c => { const d = getDate(c.created_at); return d && d >= day && d < next; }).length;
    return {
      label: day.toLocaleDateString("en", { weekday: "short", timeZone: companyTimezone }),
      count,
    };
  });

  // ── Weekly chart: last 4 weeks ─────────────────────────────────
  const weeklyData = Array.from({ length: 4 }, (_, i) => {
    const wStart = new Date(weekStart.getTime() + (i - 3) * 7 * 86400000);
    const wEnd   = new Date(wStart.getTime() + 7 * 86400000);
    const count  = clients.filter(c => { const d = getDate(c.created_at); return d && d >= wStart && d < wEnd; }).length;
    return { label: `${t("op_stats_week_label")} ${i + 1}`, count };
  });

  const statCards = [
    { label: t("op_stats_total"),   value: totalClients, color: "bg-orange-500" },
    { label: t("op_stats_today"),   value: todayClients, color: "bg-green-500"  },
    { label: t("op_stats_week"),    value: weekClients,  color: "bg-blue-500"   },
    { label: t("op_stats_deleted"), value: deleted,      color: "bg-red-400"    },
  ];

  if (loading) return (
    <div className="p-4 flex flex-col items-center justify-center py-20 gap-3">
      <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
      <p className="text-gray-400 text-sm">{t("loading")}</p>
    </div>
  );

  return (
    <div className="p-4 space-y-5 max-w-lg mx-auto">

      <h1 className="text-xl font-bold text-gray-800 pt-1">{t("op_stats_title")}</h1>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3">
        {statCards.map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl shadow-sm p-4">
            <div className={`w-8 h-8 ${color} rounded-xl flex items-center justify-center mb-3`}>
              <BarChart2 className="w-4 h-4 text-white" />
            </div>
            <p className="text-2xl font-extrabold text-gray-800">{value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Daily chart */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">{t("op_stats_daily_chart")}</h3>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={dailyData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} />
            <Bar dataKey="count" name={t("op_stats_count")} fill="#f97316" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Weekly chart */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">{t("op_stats_weekly_chart")}</h3>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={weeklyData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} />
            <Bar dataKey="count" name={t("op_stats_count")} fill="#fb923c" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE 4 — My Profile
// ═══════════════════════════════════════════════════════════════════
function ProfilePage({ currentUser, t, isDarkMode, toggleDarkMode }) {
  const [profile,     setProfile]     = useState(null);
  const [showPwForm,  setShowPwForm]  = useState(false);
  const [pwForm,      setPwForm]      = useState({ current: "", next: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState("");
  const { companyId } = useAuth();

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, "companies", companyId, "users", currentUser.uid));
        if (snap.exists()) setProfile(snap.data());
      } catch (err) {
        console.error("profile load:", err.message);
      }
    }
    load();
  }, [companyId, currentUser.uid]);

  async function handleChangePassword() {
    setError("");
    if (!pwForm.current.trim())   return setError("Current password is required.");
    if (pwForm.next.length < 6)   return setError("New password must be at least 6 characters.");
    if (pwForm.next !== pwForm.confirm) return setError(t("op_profile_pw_mismatch"));

    setSaving(true);
    try {
      const auth       = getAuth();
      const user       = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, pwForm.current);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, pwForm.next);
      setSuccess(t("op_profile_pw_updated"));
      setPwForm({ current: "", next: "", confirm: "" });
      setShowPwForm(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Current password is incorrect.");
      } else {
        setError("Error: " + err.message);
      }
    }
    setSaving(false);
  }

  return (
    <div className="p-4 space-y-5 max-w-lg mx-auto">

      <h1 className="text-xl font-bold text-gray-800 pt-1">{t("op_profile_title")}</h1>

      {/* Feedback */}
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 text-sm flex gap-2"><CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />{success}</div>}

      {/* Profile card */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-xl">
            {(profile?.first_name?.[0] || currentUser.email?.[0] || "?").toUpperCase()}
          </div>
          <div>
            <p className="font-bold text-gray-800 text-lg">
              {profile ? `${profile.first_name} ${profile.last_name}` : "—"}
            </p>
            <p className="text-sm text-orange-600 font-medium">{t("role_operator")}</p>
          </div>
        </div>
        <div className="space-y-3 border-t border-gray-100 pt-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
              <Mail className="w-4 h-4 text-gray-500" />
            </div>
            <div>
              <p className="text-xs text-gray-400">{t("form_email")}</p>
              <p className="text-sm font-medium text-gray-700">{currentUser.email}</p>
            </div>
          </div>
          {profile?.phone && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400">{t("form_phone")}</p>
                <p className="text-sm font-medium text-gray-700">{profile.phone}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Change password */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <button onClick={() => { setShowPwForm(p => !p); setError(""); }}
          className="w-full flex items-center justify-between px-5 py-4 active:bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <Lock className="w-4 h-4 text-orange-600" />
            </div>
            <span className="text-sm font-semibold text-gray-700">{t("op_profile_change_pw")}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showPwForm ? "rotate-180" : ""}`} />
        </button>

        {showPwForm && (
          <div className="px-5 pb-5 space-y-3 border-t border-gray-100">
            {/* Current password */}
            <div className="pt-3">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_profile_current_pw")}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type={showCurrent ? "text" : "password"} value={pwForm.current}
                  onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                  className="w-full pl-11 pr-11 border border-gray-200 rounded-xl py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400" />
                <button type="button" onClick={() => setShowCurrent(p => !p)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {/* New password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_profile_new_pw")}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type={showNew ? "text" : "password"} value={pwForm.next}
                  onChange={e => setPwForm(p => ({ ...p, next: e.target.value }))}
                  className="w-full pl-11 pr-11 border border-gray-200 rounded-xl py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400" />
                <button type="button" onClick={() => setShowNew(p => !p)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {/* Confirm password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("op_profile_confirm_pw")}</label>
              <input type="password" value={pwForm.confirm}
                onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <button onClick={handleChangePassword} disabled={saving}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 mt-1">
              {saving
                ? <><Loader2 className="w-4 h-4 animate-spin" />{t("op_profile_updating")}</>
                : <><CheckCircle className="w-4 h-4" />{t("op_profile_change_pw")}</>
              }
            </button>
          </div>
        )}
      </div>

      {/* Appearance — dark mode */}
      <div className={`rounded-2xl shadow-sm p-5 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
        <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
          <Moon className="w-4 h-4" /> {t("vendor_profile_appearance")}
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{t("vendor_profile_dark_mode")}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t("vendor_profile_dark_hint")}</p>
          </div>
          <button
            onClick={toggleDarkMode}
            className={`relative inline-flex items-center rounded-full w-11 h-6 transition-colors focus:outline-none ${isDarkMode ? "bg-blue-600" : "bg-gray-300"}`}>
            <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${isDarkMode ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default OperatorDashboard;
